import { useMutation } from "@tanstack/react-query";
import { SaveIcon } from "lucide-react";
import { useState } from "react";

import type { WorkspaceAdminResponse } from "../../shared/schemas";
import { ApiClientError, updateWorkspaceMemberAccess } from "../api/client";
import { AddWorkspaceUserDialog } from "./AddWorkspaceUserDialog";
import { ToolSelection } from "./WorkspaceToolSelection";
import { Alert, AlertDescription } from "./ui/alert";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

export { AddWorkspaceUserDialog };

function AccessMutationAlert({ error }: { error: Error | null }) {
  const message =
    error instanceof ApiClientError
      ? error.message
      : error
        ? "Tool access could not be updated. Try again."
        : null;
  return message ? (
    <Alert role="alert" variant="destructive">
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  ) : null;
}

export function ManageWorkspaceUserAccessDialog({
  isOnline,
  member,
  organizationId,
  tools,
  onUpdated,
}: {
  isOnline: boolean;
  member: WorkspaceAdminResponse["members"][number];
  organizationId: string;
  tools: WorkspaceAdminResponse["tools"];
  onUpdated: () => Promise<void> | void;
}) {
  const [open, setOpen] = useState(false);
  const [toolAccess, setToolAccess] = useState(member.toolAccess);
  const mutation = useMutation({
    mutationFn: () =>
      updateWorkspaceMemberAccess(organizationId, member.membershipId, { toolAccess }),
    onSuccess: async () => {
      await onUpdated();
      setOpen(false);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          setToolAccess(member.toolAccess);
          mutation.reset();
        }
      }}
    >
      <DialogTrigger render={<Button size="sm" type="button" variant="outline" />}>
        Manage access
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Manage tool access</DialogTitle>
          <DialogDescription>
            Choose which tools {member.user.displayName} can open.
          </DialogDescription>
        </DialogHeader>
        <ToolSelection
          disabled={mutation.isPending}
          selected={toolAccess}
          tools={tools}
          onChange={setToolAccess}
        />
        <AccessMutationAlert error={mutation.error} />
        <DialogFooter>
          <DialogClose
            disabled={mutation.isPending}
            render={<Button type="button" variant="ghost" />}
          >
            Cancel
          </DialogClose>
          <Button
            disabled={mutation.isPending || !isOnline}
            type="button"
            onClick={() => {
              mutation.mutate();
            }}
          >
            <SaveIcon aria-hidden="true" data-icon="inline-start" /> Save access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
